import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CopyIcon, SearchIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Card,
  CardBody,
  Code,
  Flex,
  Heading,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { updateSelectedInstances } from '../../../../redux/providerBuilder/selectedInstances';
import { getSelectedInstances } from '../../../../redux/providerBuilder/selectedInstances/selectors';
import { getSelectedNetworkRequest } from '../../../../redux/providerBuilder/selectedNetworkRequest/selectors';
import { updateActiveStep } from '../../../../redux/providerBuilder/stepperDetails';
import { getActiveStep } from '../../../../redux/providerBuilder/stepperDetails/selectors';
import { useDebounce } from '../../../../utils/logs/send-logs';
import {
  checkIfVariableNameExists,
  escapeRegExp,
  getMatchIndexes,
  getValueByPath,
  isValidVariableName,
} from '../../../../utils/provider-builder/instance-selection-utils';
import { NonJsonNavigationButtons } from './ non-json-navigation-buttons.component';
import { JsonNavigationButtons } from './json-navigation-buttons.component';
import OptimizedJsonViewerWithWorker from './optimized-json-viewer-with-worker.component';
import ReportIssueModal from './report-issue-modal.component';
import { SelectedInstancesTable } from './selected-instances-table.component';
import { getAppType } from '../../../../redux/appBuilder/stepperDetails/selectors';
import { FaMagic } from 'react-icons/fa';
import { getFirebaseIdToken } from '../../../../utils/utils';
import { fetchPointsOfInterest } from '../../../../utils/provider-builder/get-points-of-interests';
import { getSelectedNetworkIndex } from '../../../../redux/providerBuilder/selectedNetworkRequest/selectors';
import AiSuggestions from '../request-selection-new/request-details/ai-suggestions';

interface SelectDataProps {
  searchText: string;
  setActiveStepInnerForm: (value: any) => void;
}

export const SelectData = (props: SelectDataProps) => {
  const toast = useToast();
  const dispatch = useAppDispatch();
  const appType = useAppSelector(getAppType);

  const activeStep = useAppSelector(getActiveStep);
  const selectedNetworkRequest = useAppSelector(getSelectedNetworkRequest);
  const selectedNetworkIndex = useAppSelector(getSelectedNetworkIndex);
  const selectedInstances = useAppSelector(getSelectedInstances);
  const selectedInstancesRef = useRef(selectedInstances);

  const { setActiveStepInnerForm, searchText } = props;

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isEdit = searchParams.get('isEdit');
  const { manualVerificationFlow, sessionId } = location.state ?? {};

  const navigate = useNavigate();

  const [sections, setSections] = useState<any>([]);
  const [queryInstanceCount, setQueryInstanceCount] = useState<number>(0);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [selectedQueryInstance, setSelectedQueryInstance] = useState<number>(0);
  const [searchInstanceInput, setSearchInstanceInput] = useState(searchText);
  const debouncedSearchInstanceInput = useDebounce(searchInstanceInput, 1000);

  const [timerId, setTimerId] = useState<NodeJS.Timeout | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [pointsOfInterestLoading, setPointsOfInterestLoading] = useState(false);

  useEffect(() => {
    if (timerId) {
      clearTimeout(timerId);
    }

    const newTimerId = setTimeout(() => {
      handleSelectNetworkRequest();
    }, 500);
    setTimerId(newTimerId);
    return () => {
      clearTimeout(newTimerId);
    };
  }, [debouncedSearchInstanceInput, selectedNetworkIndex]);

  useEffect(() => {
    selectedInstancesRef.current = selectedInstances;
  }, [selectedInstances]);

  // set selected text to the selectedText state
  const handleInstanceSelect = () => {
    const selection = window.getSelection()?.toString();
    console.log('selection', selection);
    if (selection?.length) {
      setSearchInstanceInput(selection);
    }
  };

  function handleSelectNetworkRequest(): void {
    const responseBody = selectedNetworkRequest[selectedNetworkIndex]?.response?.body;
    const chunks: {
      t: any;
      marked: boolean;
      i?: number;
      start?: number;
      last?: number;
      regx?: any;
      jsonPath?: string;
    }[] = [];
    let last = 0;
    let count = 0;
    if (debouncedSearchInstanceInput.length) {
      if (selectedNetworkRequest[selectedNetworkIndex]?.contentType?.includes('json')) {
        handleNext(true);
        return;
      }

      const searchQueryInputsize = debouncedSearchInstanceInput.length;
      const matchIndexs = getMatchIndexes(responseBody, debouncedSearchInstanceInput);
      if (
        matchIndexs.length === 0 &&
        selectedNetworkRequest[selectedNetworkIndex]?.contentType?.includes('html')
      ) {
        toast({
          title: 'No Match Found. Please try again',
          status: 'error',
          duration: 2000,
          isClosable: true,
          position: 'top-right',
        });
        return;
      }

      if (selectedNetworkRequest[selectedNetworkIndex]?.contentType?.includes('html')) {
        matchIndexs.forEach((index) => {
          const start = index;
          count += 1;
          chunks.push({
            t: responseBody.substring(last, start),
            marked: false,
          });
          last = start + searchQueryInputsize;
          chunks.push({
            i: count,
            t: responseBody.substring(start, last),
            marked: true,
            start,
            last,
          });
        });
      }
    }

    setSelectedQueryInstance(count ? 1 : 0);

    chunks.push({ t: responseBody.substring(last), marked: false });
    setSections(chunks);
    setQueryInstanceCount(count);

    setTimeout(() => {
      document?.getElementById('queryMatch1')?.scrollIntoView();
      document?.getElementById('requestDataTitle')?.scrollIntoView();
    }, 100);
  }

  function addSelectedJsonInstanceToList(name: string, jsonData: any) {
    let variableName = name;
    if (!variableName.trim() || !isValidVariableName(variableName)) {
      return toast({
        title: 'Error',
        description: 'Invalid key name',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    }

    if (checkIfVariableNameExists(selectedInstancesRef.current, variableName)) {
      const randomNumber = Math.floor(10000 + Math.random() * 90000);
      variableName = `${variableName}_${randomNumber}`;
    }

    const xPath = '';

    const selectedInstData = { ...jsonData };

    if (selectedInstData?.regx) {
      const regexMat = /({{.*?}})/g;
      const matches = selectedInstData.regx.match(regexMat) || [];
      if (matches.length > 0) {
        selectedInstData.regx = selectedInstData?.regx?.replace(
          matches[0],
          '{{' + variableName + '}}'
        );
      }
    }

    const regex = selectedInstData?.regx;

    const newSelectedInstanceList = {
      data: selectedInstData?.data,
      variableName: variableName,
      responseSelection: {
        xPath: xPath,
        jsonPath: selectedInstData?.jsonPath ?? '',
        responseMatch: regex,
        invariant: selectedInstData?.invariantChecked,
        hash: selectedInstData?.isPrivateInformation ? 'oprf' : '',
        matchType: 'greedy',
        description: selectedInstData?.description,
        requestIndex: selectedNetworkIndex,
      },
    };
    dispatch(updateSelectedInstances(newSelectedInstanceList));
  }

  function handleItemClick(
    path: string,
    key: string = '',
    isUniqueToUser: boolean,
    isPrivateInformation: boolean,
    description: string = ''
  ) {
    const jsonPath = `$${path}`;

    const pathAlreadyExists = selectedInstancesRef.current.some(
      (instance) => instance.responseSelection.jsonPath === jsonPath
    );

    if (pathAlreadyExists) {
      return toast({
        title: 'Error',
        description: 'This Variable is already added',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    }

    let selectedValue = getValueByPath(
      JSON.parse(selectedNetworkRequest[selectedNetworkIndex].response.body),
      path
    );

    let regx;

    if (typeof selectedValue === 'object') {
      selectedValue = JSON.stringify(selectedValue);
      regx = `"${path.split('.').pop()}":${escapeRegExp(
        selectedValue?.replace(selectedValue, 'CLAIM_DATA')
      )?.replace('CLAIM_DATA', '{{CLAIM_DATA}}')}`;
    } else if (typeof selectedValue === 'number' || typeof selectedValue === 'boolean') {
      const selectedValueString = `${selectedValue}`;
      regx = `"${path.split('.').pop()}":${escapeRegExp(
        selectedValueString?.replace(selectedValueString, 'CLAIM_DATA')
      )?.replace('CLAIM_DATA', '{{CLAIM_DATA}}')}`;
    } else {
      regx = `"${path.split('.').pop()}":"${escapeRegExp(
        selectedValue?.replace(selectedValue, 'CLAIM_DATA')
      )?.replace('CLAIM_DATA', '{{CLAIM_DATA}}')}"`;
    }

    addSelectedJsonInstanceToList(key, {
      data: selectedValue,
      jsonPath,
      regx,
      invariantChecked: isUniqueToUser,
      isPrivateInformation,
      description,
      requestIndex: selectedNetworkIndex,
    });
  }

  function handleNext(highlightFirst: boolean = false) {
    const allHighlights = document.querySelectorAll<HTMLElement>(
      '.highlight-searched-json-value'
    );
    if (allHighlights.length > 0) {
      if (currentIndex >= 0 && currentIndex < allHighlights.length) {
        allHighlights[currentIndex].style.border = '';
        allHighlights[currentIndex].style.backgroundColor = '';
        allHighlights[currentIndex].style.borderRadius = '';
        allHighlights[currentIndex].style.padding = '0px';
      }

      let nextIndex;
      if (highlightFirst) {
        nextIndex = 0;
      } else {
        nextIndex = (currentIndex + 1) % allHighlights.length;
      }

      setCurrentIndex(nextIndex);
      allHighlights[nextIndex].style.border = '2px solid #FF6F61';
      allHighlights[nextIndex].style.backgroundColor = '#FF6F61';
      allHighlights[nextIndex].style.borderRadius = '99999px';
      allHighlights[nextIndex].style.padding = '0 5px';
      allHighlights[nextIndex].scrollIntoView({ behavior: 'smooth' });
    }
  }

  function handlePrevious() {
    const allHighlights = document.querySelectorAll<HTMLElement>(
      '.highlight-searched-json-value'
    );
    if (allHighlights.length > 0) {
      if (currentIndex >= 0 && currentIndex < allHighlights.length) {
        allHighlights[currentIndex].style.border = '';
        allHighlights[currentIndex].style.backgroundColor = '';
        allHighlights[currentIndex].style.borderRadius = '';
        allHighlights[currentIndex].style.padding = '0px';
      }

      const prevIndex = currentIndex === 0 ? allHighlights.length - 1 : currentIndex - 1;
      setCurrentIndex(prevIndex);

      allHighlights[prevIndex].style.border = '2px solid #FF6F61';
      allHighlights[prevIndex].style.backgroundColor = '#FF6F61';
      allHighlights[prevIndex].style.borderRadius = '99999px';
      allHighlights[prevIndex].style.padding = '0 5px';

      allHighlights[prevIndex].scrollIntoView({ behavior: 'smooth' });
    }
  }

  function handleGoBack() {
    if (manualVerificationFlow) {
      navigate('/manual-verification', {
        state: { manualVerificationFlow: true, sessionId },
      });
    } else if (isEdit) {
      navigate(-1);
    }
    if (appType === 'ZK_FETCH') setActiveStepInnerForm(2);
    else setActiveStepInnerForm(0);
  }

  function copyResponseBody() {
    navigator.clipboard
      .writeText(selectedNetworkRequest[selectedNetworkIndex]?.response?.body ?? '')
      .then(() => {
        toast({
          title: 'Response body copied',
          status: 'success',
          duration: 1000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .catch((err) => {
        console.error('Failed to copy text to clipboard', err);
      });
  }

  const renderNavigationButtons = () => {
    if (selectedNetworkRequest[selectedNetworkIndex]?.contentType?.includes('json')) {
      return (
        <JsonNavigationButtons
          handlePrevious={handlePrevious}
          handleNext={() => handleNext()}
          isDisabled={
            selectedNetworkRequest[selectedNetworkIndex]?.response?.body?.length > 50000
          }
        />
      );
    } else {
      return (
        <NonJsonNavigationButtons
          selectedQueryInstance={selectedQueryInstance}
          setSelectedQueryInstance={setSelectedQueryInstance}
          queryInstanceCount={queryInstanceCount}
          debouncedSearchInstanceInput={debouncedSearchInstanceInput}
          sections={sections}
        />
      );
    }
  };

  const MAX_RETRY_COUNT = 3;

  const getPointsOfInterestUsingAI = async () => {
    try {
      setPointsOfInterestLoading(true);

      const accessToken = (await getFirebaseIdToken()) ?? '';

      let attempt = 0;
      let result;

      while (attempt < MAX_RETRY_COUNT) {
        try {
          result = await fetchPointsOfInterest(
            selectedNetworkRequest[selectedNetworkIndex]?.response?.body,
            selectedNetworkRequest[selectedNetworkIndex]?.contentType,
            accessToken
          );

          console.log({ result });

          if (result?.isSuccess && result?.dataPoints?.length) {
            break; // Exit loop if successful
          }

          throw new Error('No data points found');
        } catch (error: any) {
          attempt++;
          console.log(`Attempt ${attempt} failed: ${error.message}`);

          if (attempt < MAX_RETRY_COUNT) {
            toast({
              title: 'Warning',
              description: `No points of interest found. Retrying attempt ${attempt}/${MAX_RETRY_COUNT}...`,
              status: 'warning',
              duration: 3000,
              isClosable: true,
              position: 'top-right',
            });
          } else {
            throw error; // Throw error if max attempts are reached
          }

          await new Promise((resolve) => setTimeout(resolve, 2000)); // Wait before retrying
        }
      }

      console.log({ result });

      toast({
        title: 'Success',
        description: `${result?.dataPoints?.length} points of interest fetched successfully`,
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });

      if (selectedNetworkRequest[selectedNetworkIndex]?.contentType?.includes('json')) {
        const responseBody = JSON.parse(
          selectedNetworkRequest[selectedNetworkIndex]?.response?.body
        );
        result?.dataPoints?.forEach((dataPoint: any) => {
          const valueByPath = getValueByPath(
            responseBody,
            dataPoint.jsonPath.substring(1)
          );
          if (valueByPath?.toString() === dataPoint.value) {
            let variableName = dataPoint.variableName;
            if (checkIfVariableNameExists(selectedInstancesRef.current, variableName)) {
              const randomNumber = Math.floor(10000 + Math.random() * 90000);
              variableName = `${variableName}_${randomNumber}`;
            }
            const newSelectedInstanceList = {
              data: dataPoint.value,
              variableName: variableName,
              aiGenerated: true,
              responseSelection: {
                xPath: '',
                jsonPath: dataPoint.jsonPath,
                responseMatch: dataPoint.responseMatch,
                invariant: false,
                isPrivateInformation: false,
                matchType: 'greedy',
                description: dataPoint.longDescription,
                requestIndex: selectedNetworkIndex,
              },
            };
            dispatch(updateSelectedInstances(newSelectedInstanceList));
          }
        });
      } else {
        result?.dataPoints?.forEach((dataPoint: any) => {
          let variableName = dataPoint.variableName;
          if (checkIfVariableNameExists(selectedInstancesRef.current, variableName)) {
            const randomNumber = Math.floor(10000 + Math.random() * 90000);
            variableName = `${variableName}_${randomNumber}`;
          }
          const newSelectedInstanceList = {
            data: dataPoint.value,
            variableName,
            aiGenerated: true,
            responseSelection: {
              xPath: dataPoint.xpath,
              jsonPath: '',
              responseMatch: dataPoint.responseMatch,
              invariant: false,
              isPrivateInformation: false,
              matchType: 'greedy',
              description: dataPoint.longDescription,
              requestIndex: selectedNetworkIndex,
            },
          };
          console.log({ newSelectedInstanceList });

          console.log('oldSelectedInstanceList', newSelectedInstanceList);
          dispatch(updateSelectedInstances(newSelectedInstanceList));
        });
      }
    } catch (error: any) {
      console.log({ error });
      toast({
        title: 'Error',
        description:
          error?.message ?? 'Some error occured while fetching points of interest',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setPointsOfInterestLoading(false);
    }
  };

  return (
    <>
      <Box width="100%" height="100%">
        <Flex
          direction="column"
          pt={4}
          h="calc(100vh - 210px)"
          overflowY="scroll"
          overflowX="hidden"
        >
          {selectedNetworkRequest[selectedNetworkIndex]?.contentType?.includes('json') &&
            selectedNetworkRequest[selectedNetworkIndex]?.response.body?.length >
              50000 && (
              <Box mb={2}>
                <Alert status="warning" borderRadius={5} color="blackAlpha">
                  <AlertIcon />
                  {/* // add message that since it's alarge json, we disabled exapnd and collapse use cmd +f search */}
                  <Text>
                    Since the JSON response is large, we have disabled the expand and
                    collapse feature. You can still search for values using cmd+f.
                  </Text>
                </Alert>
              </Box>
            )}
          <Flex direction="column" w="full" gap={5}>
            <Flex direction="column" px={1} borderBottom="1px solid #EFF1F5">
              <Heading className="regular-font">Select Data</Heading>
              <Text className="small-font" lineHeight="24px" color="#98A2B3">
                Click on the key to add it as required data in the proof
              </Text>
            </Flex>
            <Flex>
              <SelectedInstancesTable />
            </Flex>
          </Flex>
          <AiSuggestions
            requestBody={selectedNetworkRequest[selectedNetworkIndex]?.response?.body}
            contentType={selectedNetworkRequest[selectedNetworkIndex]?.contentType}
          />
          <Box flex={1} position={'relative'}>
            <Flex direction="column">
              <Box>
                <Flex direction="column" gap={2} py={5} px={1}>
                  <Heading className="medium-font">Body</Heading>
                  <Box height="100%" position="relative" backgroundColor="gray.50">
                    <Flex>
                      <Card w="100%" backgroundColor="gray.50" borderRadius={0}>
                        <CardBody>
                          <Flex alignItems="center">
                            <Flex width="70%" gap={5}>
                              <InputGroup maxWidth="500px">
                                <InputLeftElement>
                                  <SearchIcon />
                                </InputLeftElement>
                                <Input
                                  _placeholder={{
                                    color: '#667085',
                                    fontSize: '14px',
                                  }}
                                  // _placeholder={{ color: '#000', fontSize: '15px' }}
                                  fontSize="clamp(11px, calc(0.7 * 1.6), 20px)"
                                  borderColor="#000"
                                  maxWidth="500px"
                                  value={searchInstanceInput}
                                  onChange={(e) => setSearchInstanceInput(e.target.value)}
                                  isDisabled={
                                    selectedNetworkRequest[
                                      selectedNetworkIndex
                                    ]?.contentType?.includes('json') &&
                                    selectedNetworkRequest[selectedNetworkIndex]?.response
                                      ?.body?.length > 50000
                                  }
                                  placeholder={
                                    selectedNetworkRequest[
                                      selectedNetworkIndex
                                    ]?.contentType?.includes('json') &&
                                    selectedNetworkRequest[selectedNetworkIndex]?.response
                                      ?.body?.length > 50000
                                      ? 'Press cmd+f to search'
                                      : 'Search the data you want to verify'
                                  }
                                />
                              </InputGroup>
                              {renderNavigationButtons()}
                            </Flex>
                            <Flex
                              width="30%"
                              alignItems="center"
                              justifyContent="flex-end"
                              gap={2}
                            >
                              {
                                <Tooltip
                                  label="Get points of interest using AI"
                                  hasArrow
                                  placement="top"
                                >
                                  <IconButton
                                    aria-label="copy"
                                    onClick={getPointsOfInterestUsingAI}
                                    bgColor="black"
                                    _hover={{
                                      bgColor: 'black',
                                      transform: 'scale(1.1)',
                                    }}
                                    icon={<FaMagic color="white" />}
                                    isLoading={pointsOfInterestLoading}
                                  />
                                </Tooltip>
                              }

                              <Tooltip
                                label="Copy response body"
                                hasArrow
                                placement="top"
                              >
                                <IconButton
                                  aria-label="copy"
                                  onClick={copyResponseBody}
                                  bgColor="black"
                                  _hover={{
                                    bgColor: 'black',
                                    transform: 'scale(1.1)',
                                  }}
                                  icon={<CopyIcon color="white" />}
                                />
                              </Tooltip>
                              <ReportIssueModal
                                showNew
                                searchInstanceInput={debouncedSearchInstanceInput}
                              />
                            </Flex>
                          </Flex>
                        </CardBody>
                      </Card>
                    </Flex>

                    <Box
                      backgroundColor="gray.50"
                      borderRadius="5px"
                      p={2}
                      pt={4}
                      minHeight="30vh"
                      maxHeight="50vh"
                      overflowY="scroll"
                    >
                      {sections ? (
                        <>
                          {selectedNetworkRequest[
                            selectedNetworkIndex
                          ]?.contentType?.includes('json') ? (
                            <OptimizedJsonViewerWithWorker
                              jsonString={
                                selectedNetworkRequest[selectedNetworkIndex].response.body
                              }
                              onItemClick={handleItemClick}
                              searchTerm={debouncedSearchInstanceInput}
                              isDisabled={
                                selectedNetworkRequest[selectedNetworkIndex].response.body
                                  ?.length > 50000
                              }
                            />
                          ) : (
                            <>
                              <Text
                                onMouseUp={handleInstanceSelect}
                                style={{
                                  fontVariantNumeric: 'tabular-nums',
                                }}
                                color="#000"
                                fontWeight="400"
                                className="font-roboto-mono-regular small"
                              >
                                {sections.map((section: any, i: any) =>
                                  section.marked ? (
                                    <span
                                      key={i}
                                      id={`queryMatch${section.i}`}
                                      style={{
                                        backgroundColor: '#ffd180',
                                        cursor: 'pointer',
                                        borderColor:
                                          section.i === selectedQueryInstance
                                            ? '#FF6F61'
                                            : undefined,
                                        borderWidth: '2px',
                                        fontWeight:
                                          section.i === selectedQueryInstance
                                            ? 'bold'
                                            : undefined,
                                        padding:
                                          section.i === selectedQueryInstance
                                            ? '0.2rem'
                                            : '0.1rem',
                                        borderRadius: '15px',
                                      }}
                                      onClick={() => setSelectedQueryInstance(section.i)}
                                    >
                                      {section.t}
                                    </span>
                                  ) : (
                                    section.t
                                  )
                                )}
                              </Text>
                            </>
                          )}
                        </>
                      ) : (
                        <Code
                          style={{
                            maxHeight: '600px',
                            overflow: 'auto',
                          }}
                          className="font-roboto-mono-regular"
                        >
                          {selectedNetworkRequest[selectedNetworkIndex]?.response?.body}
                        </Code>
                      )}
                    </Box>
                  </Box>
                </Flex>
              </Box>
            </Flex>
          </Box>
        </Flex>
        <Box w="full" mt={2} backgroundColor="gray.50" px={30} py={4}>
          <Flex w="full" justifyContent="space-between">
            <Button
              onClick={handleGoBack}
              backgroundColor="transparent"
              border="1px solid gray"
              borderRadius={10}
              fontWeight="normal"
              _hover={{
                fontWeight: 'bold',
                transform: 'scale(1.05)',
              }}
            >
              Back
            </Button>
            <Flex alignItems="center" gap={3}>
              {appType !== 'ZK_FETCH' && (
                <Text color="gray.500" className="small-font">
                  3/7
                </Text>
              )}
              <Tooltip
                label={
                  selectedInstances.length
                    ? ''
                    : 'Please add at least one data instance to proceed'
                }
              >
                <Button
                  backgroundColor="black"
                  color="white"
                  border="1px solid black"
                  borderRadius={10}
                  fontWeight="normal"
                  _hover={{
                    transform: 'scale(1.05)',
                  }}
                  isLoading={buttonLoading}
                  isDisabled={!selectedInstances.length}
                  onClick={() => {
                    setButtonLoading(true);
                    if (appType === 'ZK_FETCH') {
                      setActiveStepInnerForm(5);
                      return;
                    }
                    dispatch(updateActiveStep(activeStep + 1));
                  }}
                >
                  Next
                </Button>
              </Tooltip>
            </Flex>
          </Flex>
        </Box>
      </Box>
    </>
  );
};
